import locale from '@glu/locale';
import { generateTime, getHours, getTime } from './time';
import { getDaysPerMonth } from './date';

export const MINUTES = 'MINUTES';
export const HOURLY = 'HOURS';
export const DAILY = 'DAYS';
export const WEEKLY = 'WEEKS';
export const MONTHLY = 'MONTHS';
export const SEMIMONTHLY = 'SEMIMONTHLY';
export const YEARLY = 'YEARS';

export const FIRST = 'FIRST';
export const SECOND = 'SECOND';
export const THIRD = 'THIRD';
export const FOURTH = 'FOURTH';
export const LAST = 'LAST';

export const MON = 'MONDAY';
export const TUE = 'TUESDAY';
export const WED = 'WEDNESDAY';
export const THU = 'THURSDAY';
export const FRI = 'FRIDAY';
export const SAT = 'SATURDAY';
export const SUN = 'SUNDAY';
export const DAY = 'DATE';

export const getDefaultType = ({
  minutes, hourly, daily, weekly, monthly, semiMonthly, yearly
}) => {
  if (minutes) return MINUTES;
  if (hourly) return HOURLY;
  if (daily) return DAILY;
  if (weekly) return WEEKLY;
  if (monthly) return MONTHLY;
  if (semiMonthly) return SEMIMONTHLY;
  if (yearly) return YEARLY;

  return false;
};

export const generateId = () => (
  Math.random().toString(36).substr(2, 9)
);

export const getRecurrenceOptions = ({
  minutes, hourly, daily, weekly, monthly, semiMonthly, yearly
}) => {
  const options = [];
  if (minutes) {
    options.push({
      label: locale.get('schedulerReact.minutes'),
      value: MINUTES
    });
  }
  if (hourly) {
    options.push({
      label: locale.get('schedulerReact.hourly'),
      value: HOURLY
    });
  }
  if (daily) {
    options.push({
      label: locale.get('schedulerReact.daily'),
      value: DAILY
    });
  }
  if (weekly) {
    options.push({
      label: locale.get('schedulerReact.weekly'),
      value: WEEKLY
    });
  }
  if (monthly) {
    options.push({
      label: locale.get('schedulerReact.monthly'),
      value: MONTHLY
    });
  }
  if (semiMonthly) {
    options.push({
      label: locale.get('schedulerReact.semimonthly'),
      value: SEMIMONTHLY
    });
  }
  if (yearly) {
    options.push({
      label: locale.get('schedulerReact.yearly'),
      value: YEARLY
    });
  }
  return options;
};

export const generateRecurrenceObject = (recurrence, type) => {
  switch (type) {
    case MINUTES:
      return {
        nMinutes: recurrence.nMinutes
      };
    case HOURLY:
      return {
        nHours: recurrence.nHours
      };
    case DAILY:
      return recurrence.atTime
        ? {
          atTime: recurrence.atTime
        } : null;
    case WEEKLY:
      return {
        nWeeks: recurrence.nWeeks,
        onN: recurrence.daysInWeek,
        atTime: recurrence.atTime
      };
    case MONTHLY:
      return {
        nMonths: recurrence.nMonths,
        onN: recurrence.daysInMonth
      };
    case SEMIMONTHLY:
      return {
        nSemiMonths: recurrence.nSemiMonths,
        onN: recurrence.daysInSemiMonth
      };
    case YEARLY:
      return {
        onN: recurrence.daysInYear
      };
    default:
      return null;
  }
};

/**
 * Setup recurrence object by generating unique ids
 * @param recurrenceObject
 * @returns {*}
 */
export const setupRecurrence = (recurrenceObject) => recurrenceObject.map(instance => ({
  ...instance,
  id: instance.id || generateId()
}));

/**
 * Creates a day in month object
 * @param defaultDay
 * @returns {{intervalType: *, id: *, day: number}}
 */
export const generateDayInMonth = (defaultDay) => (
  {
    id: generateId(),
    day: defaultDay || 1,
    intervalType: DAY
  }
);

/**
 * Create default day with time in month object
 * @param support24
 * @param defaultValue
 * @returns {{atTime: *, intervalType: *, id: *, day: number}}
 */
export const generateDayTimeInMonth = (support24, defaultValue = {}) => (
  {
    ...generateDayInMonth(defaultValue.day),
    atTime: defaultValue.atTime ? defaultValue.atTime : generateTime(support24)
  }
);

/**
 * Get default day in month object
 * @param canSetTime
 * @param support24
 * @param defaultValue
 * @returns {*}
 */
export const getDefaultDayInMonth = (canSetTime, support24, defaultValue) => (
  canSetTime
    ? generateDayTimeInMonth(support24, defaultValue)
    : generateDayInMonth(defaultValue.day)
);

/**
 * Create default day in year object
 * @returns {{month: number, id: *, day: number}}
 */
export const generateDayInYear = () => (
  {
    id: generateId(),
    day: 1,
    month: 1
  }
);

/**
 * Create default day with time in year object
 * @returns {{month: number, id: *, day: number}}
 */
export const generateDayTimeInYear = (support24) => (
  {
    ...generateDayInYear(),
    atTime: generateTime(support24)
  }
);

/**
 * Get default day in year object
 * @param canSetTime
 * @param support24
 * @returns {{month: number, id: *, day: number}}
 */
export const getDefaultDayInYear = (canSetTime, support24) => (
  canSetTime ? generateDayTimeInYear(support24) : generateDayInYear()
);

/**
 * Get default day given if interval type changes
 * @param currentInterval
 * @param newInterval
 * @param dayValue
 * @returns {string}
 */
export const getDefaultDayForInterval = (currentInterval, newInterval, dayValue) => (
  currentInterval === DAY && newInterval !== DAY
    ? LAST
    : currentInterval !== DAY && newInterval === DAY
      ? LAST
      : dayValue
);

/**
 * Get valid day for month of 1 if day isn't in month, otherwise get given day
 * @param month
 * @param day
 * @returns {string}
 */
export const getValidDayForMonth = (month, day) => (
  day > getDaysPerMonth(month) ? 1 : day
);

/**
 * Return values for the hours dropdown list in AM/PM format
 * @param meridiem
 * @param min
 * @param max
 * @returns {*[]}
 */
export const getHourOptions = (meridiem, min, max) => {
  const hourList = [];
  const fromTime = min ? getTime(min) : {};
  const toTime = max ? getTime(max) : {};
  let from = 0;
  let to = 0;

  /**
   * Set maximum initial limits if meridians in the different parts of the day
   */
  if (fromTime.meridiem !== toTime.meridiem || !min || !max) {
    from = 12;
    to = 11;
  }

  /**
   * Set the limits if the current time
   * is in the same part of the day
   * like the selected by user AM/PM
   */
  if (meridiem === fromTime.meridiem) {
    from = fromTime.hours;
  }
  if (meridiem === toTime.meridiem) {
    to = toTime.hours;
  }

  /**
   * Set 12 as first element of the list
   */
  if (from === 12) {
    from = 1;
    hourList.push({
      label: 12,
      value: 12
    });
  }

  return [
    ...hourList,
    ...Array(to - from + (from !== 0 && to !== 0)).fill().map((_, idx) => ({
      label: from + idx < 10 ? `0${from + idx}` : from + idx,
      value: from + idx
    }))
  ];
};

/**
 * Return values for the hours dropdown list in 24 hour format
 * @param min
 * @param max
 * @returns {*[]}
 */
export const get24HourOptions = (min, max) => {
  const from = min ? getHours(min, true) : 0;
  const to = max ? getHours(max, true) : 23;

  return Array(to - from + 1).fill().map((_, idx) => ({
    label: from + idx < 10 ? `0${from + idx}` : from + idx,
    value: from + idx
  }));
};

export const ensureTwoDaysForSemiMonth = state => ({
  ...state,
  daysInSemiMonth: [
    ...state.daysInSemiMonth,
    generateDayInMonth(1),
    generateDayInMonth(2)
  ].slice(0, 2)
});
